<script setup>
import { ref, onMounted, computed } from 'vue'
import { PaperClipIcon } from '@heroicons/vue/20/solid'

const links = ref([])
const documents = ref([])
const searchQuery = ref('')

const getTimestamp = (value) => {
  const t = new Date(value || 0).getTime()
  return Number.isNaN(t) ? 0 : t
}

const groupedFolders = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  const folderMap = new Map()

  const ensureFolder = (name) => {
    if (!folderMap.has(name)) {
      folderMap.set(name, {
        name,
        links: [],
        documents: [],
        latestAt: 0,
      })
    }
    return folderMap.get(name)
  }

  for (const link of links.value) {
    const searchable = `${link.name} ${link.url}`.toLowerCase()
    if (query && !searchable.includes(query)) continue

    const folderName = link.folder_name || 'Sans dossier'
    const group = ensureFolder(folderName)
    group.links.push(link)
    group.latestAt = Math.max(group.latestAt, getTimestamp(link.created_at))
  }

  for (const doc of documents.value) {
    const searchable = `${doc.name} ${doc.url}`.toLowerCase()
    if (query && !searchable.includes(query)) continue

    const folderName = doc.folder_name || 'Sans dossier'
    const group = ensureFolder(folderName)
    group.documents.push(doc)
    group.latestAt = Math.max(group.latestAt, getTimestamp(doc.created_at))
  }

  return [...folderMap.values()]
    .map((group) => ({
      ...group,
      links: group.links.sort((a, b) => getTimestamp(b.created_at) - getTimestamp(a.created_at)),
      documents: group.documents.sort((a, b) => getTimestamp(b.created_at) - getTimestamp(a.created_at)),
    }))
    .sort((a, b) => {
      if (b.latestAt !== a.latestAt) return b.latestAt - a.latestAt
      return a.name.localeCompare(b.name, 'fr')
    })
})

const fetchPdfFiles = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/pdf')
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des fichiers PDF')
    }
    const data = await response.json()

    documents.value = data.map(item => ({
      id: item.id,
      name: item.url_image.split('/').pop(),
      url: item.url_image,
      folder_name: item.folder_name,
      created_at: item.created_at,
    }))
  } catch (error) {
    console.error(error)
  }
}

const fetchUrlLinks = async () => {
  try {
    const response = await fetch('https://api.meritemaritime-fnmm.com/member/url')
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des liens URL')
    }
    const data = await response.json()

    links.value = data.map(item => ({
      id: item.id,
      name: item.url_lien.split('/').pop(),
      url: item.url_lien,
      folder_name: item.folder_name,
      created_at: item.created_at,
    }))
  } catch (error) {
    console.error(error)
  }
}

onMounted(() => {
  fetchPdfFiles()
  fetchUrlLinks()
})
</script>

<template>
  <div>
    <div class="px-4 sm:px-0">
      <h3 class="text-base/7 font-semibold text-white">Mes documents</h3>
      <p class="mt-1 max-w-2xl text-sm/6 text-gray-400">Accédez à l'ensemble des documents et des liens par dossier.</p>
    </div>
    <div class="mt-6 border-t border-white/10">
      <div class="px-4 py-6 sm:px-0">
        <div class="mb-6">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un document ou un lien..."
            class="w-full rounded-md border border-white/20 bg-transparent px-3 py-2 text-sm text-white placeholder:text-gray-400 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
        </div>

        <div v-if="groupedFolders.length === 0" class="rounded-md border border-white/20 px-4 py-6 text-sm text-gray-400">
          Aucun document ou lien dans ce résultat.
        </div>

        <div v-for="folder in groupedFolders" :key="folder.name" class="mb-6 rounded-md border border-white/20">
          <div class="border-b border-white/10 px-4 py-3">
            <h4 class="text-sm font-semibold text-indigo-300">{{ folder.name }}</h4>
          </div>

          <div v-if="folder.links.length > 0" class="px-4 py-3">
            <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Liens</p>
            <ul role="list" class="divide-y divide-white/10 rounded-md border border-white/20">
              <li v-for="link in folder.links" :key="`link-${link.id}`" class="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                <div class="flex w-0 flex-1 items-center">
                  <PaperClipIcon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <div class="ml-4 flex min-w-0 flex-1 gap-2">
                    <span class="truncate font-medium text-white">{{ link.url }}</span>
                  </div>
                </div>
                <div class="ml-4 shrink-0">
                  <a :href="link.url" class="font-medium text-indigo-400 hover:text-indigo-300">Lien</a>
                </div>
              </li>
            </ul>
          </div>

          <div v-if="folder.documents.length > 0" class="px-4 py-3">
            <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Documents</p>
            <ul role="list" class="divide-y divide-white/10 rounded-md border border-white/20">
              <li v-for="doc in folder.documents" :key="`doc-${doc.id}`" class="flex items-center justify-between py-4 pl-4 pr-5 text-sm/6">
                <div class="flex w-0 flex-1 items-center">
                  <PaperClipIcon class="size-5 shrink-0 text-gray-400" aria-hidden="true" />
                  <div class="ml-4 flex min-w-0 flex-1 gap-2">
                    <span class="truncate font-medium text-white">{{ doc.name }}</span>
                  </div>
                </div>
                <div class="ml-4 shrink-0">
                  <a :href="doc.url" class="font-medium text-indigo-400 hover:text-indigo-300">Télécharger</a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
